import { Injectable, inject } from '@angular/core';
import { Matchmaking } from '@app/model/matchmaking';
import { SupabaseClient } from '@supabase/supabase-js';
import { UserService } from '../user-service/user.service';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';

export type CreateMatchmakingResult = {
  id: string;
  created_at: string;
  acceptor_id: string;
  task_id: string;
};

export type DenyTaskMatchmakingResult = {
  success: boolean;
  denied: boolean;
  already_processed?: boolean;
  reason?: string;
  taskId: string;
  task_id?: string;
  acceptorId: string;
  acceptor_id?: string;
  matchmaking_id?: string | null;
  isSupplyTask: boolean;
  is_supply_task?: boolean;
  deleted_count?: number;
  has_pending_matchmaking?: boolean;
  refund?: {
    refunded?: boolean;
    reason?: string;
    amount?: number;
    refund_key?: string;
    admin_balance_before?: number;
    admin_balance_after?: number;
    acceptor_balance_before?: number;
    acceptor_balance_after?: number;
  } | null;
  notification?: unknown;
};

@Injectable({
  providedIn: 'root'
})
export class MatchmakingService {

  matchmaking: Matchmaking = new Matchmaking('', new Date(), '', '', '');
  matchmakings = [];

  private supabaseClient: SupabaseClient;

  constructor(private userService: UserService) {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  async getMatchmakingsByTaskId(taskId: string) {
    const res = await this.supabaseClient.from('matchmaking').select('*').eq('task_id', taskId);
    const matchmakings = res.data;
    await Promise.all((matchmakings ?? []).map(async (matchmaking: Matchmaking) => {
      const acceptorResponse = await this.userService.getUserNameById(matchmaking.acceptor_id);
      matchmaking.acceptor_name = acceptorResponse?.data?.[0].name;
    }));
    return matchmakings;
  }

  async getMatchmakingsByAcceptorId(acceptorId: string) {
    const { data, error } = await this.supabaseClient
      .from('matchmaking')
      .select('*')
      .eq('acceptor_id', acceptorId);

    if (error) {
      throw new Error(error.message);
    }

    return data as Matchmaking[];
  }

  async createMatchmaking(taskId: string, acceptorId: string) {
    const acceptorInfo = await this.userService.getUserInfo(acceptorId);
    if (acceptorInfo?.data?.[0]?.is_super_admin) {
      throw new Error('系統金庫帳號不可加入媒合清單');
    }

    const { data, error } = await this.supabaseClient.rpc('create_matchmaking_for_available_task', {
      requested_task_id: taskId,
      requested_acceptor_id: acceptorId
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as CreateMatchmakingResult;
  }

  // 加入供給媒合清單並預扣雲幣，兩者在同一個資料庫交易中完成
  async joinSupplyMatchmaking(taskId: string) {
    const { data, error } = await this.supabaseClient.rpc('join_supply_matchmaking', {
      requested_task_id: taskId
    });

    if (error) {
      throw new Error(error.message);
    }

    return data as CreateMatchmakingResult & { deposit_amount: number };
  }

  async denyTaskMatchmaking(taskId: string, acceptorId: string) {
    const { data, error } = await this.supabaseClient.functions.invoke('deny-task-matchmaking', {
      body: {
        taskId,
        acceptorId
      }
    });

    if (error) {
      throw new Error(await this.getFunctionErrorMessage(error));
    }

    return data as DenyTaskMatchmakingResult;
  }

  private async getFunctionErrorMessage(error: any) {
    const response = error?.context ?? error?.response;
    if (response?.clone) {
      try {
        const body = await response.clone().json();
        if (body?.error) {
          return body.error;
        }
      } catch {
        try {
          const text = await response.clone().text();
          if (text) {
            return text;
          }
        } catch {
          // Fall through to the SDK error message.
        }
      }
    }

    return error?.message || '拒絕媒合失敗';
  }

  /**
   * 獲取用戶所有創建任務的待媒合列表
   * @param creatorId 創建者ID
   * @returns 待媒合列表，包含任務資訊和申請者資訊
   */
  async getPendingMatchmakingsByCreatorId(creatorId: string) {
    try {
      // 獲取用戶創建的所有任務的待媒合記錄
      const res = await this.supabaseClient
        .from('matchmaking')
        .select(`
          *,
          tasks!inner(
            id,
            title,
            is_supply,
            sub_category,
            creatorId,
            acceptorId,
            isAccepted,
            status
          )
        `)
        .eq('tasks.creatorId', creatorId)
        .is('tasks.acceptorId', null)
        .eq('tasks.isAccepted', false);

      if (res.data) {
        // 為每個媒合記錄添加申請者姓名
        const matchmakingsWithNames = await Promise.all(
          res.data.map(async (matchmaking: any) => {
            const acceptorResponse = await this.userService.getUserNameById(matchmaking.acceptor_id);
            return {
              ...matchmaking,
              acceptor_name: acceptorResponse?.data?.[0]?.name || '未知用戶',
              task_title: matchmaking.tasks.title,
              task_type: matchmaking.tasks.is_supply ? '供給' : '需求',
              task_id: matchmaking.tasks.id
            };
          })
        );
        
        return matchmakingsWithNames;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching pending matchmakings:', error);
      return [];
    }
  }

  /**
   * 獲取用戶已提出媒合要求但還在等待回覆的任務列表
   * @param acceptorId 申請者ID
   * @returns 待回覆的媒合列表，包含任務資訊和創建者資訊
   */
  async getPendingMatchmakingsByAcceptorId(acceptorId: string) {
    try {
      // 獲取用戶申請的所有待回覆媒合記錄
      const res = await this.supabaseClient
        .from('matchmaking')
        .select(`
          *,
          tasks!inner(
            id,
            title,
            is_supply,
            sub_category,
            creatorId,
            acceptorId,
            isAccepted,
            status
          )
        `)
        .eq('acceptor_id', acceptorId)
        .is('tasks.acceptorId', null)
        .eq('tasks.isAccepted', false);

      if (res.data) {
        // 為每個媒合記錄添加創建者姓名
        const matchmakingsWithNames = await Promise.all(
          res.data.map(async (matchmaking: any) => {
            const creatorResponse = await this.userService.getUserNameById(matchmaking.tasks.creatorId);
            return {
              ...matchmaking,
              creator_name: creatorResponse?.data?.[0]?.name || '未知用戶',
              task_title: matchmaking.tasks.title,
              task_type: matchmaking.tasks.is_supply ? '供給' : '需求',
              task_id: matchmaking.tasks.id
            };
          })
        );
        
        return matchmakingsWithNames;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching pending matchmakings by acceptor:', error);
      return [];
    }
  }

}
